import { v4 as uuidv4 } from 'uuid';
import {
  FunnelElement,
  ElementType,
  ElementStyles,
  HeadingElement,
  TextElement,
  ButtonElement,
  ImageElement,
  SpacerElement,
  DividerElement,
} from '../types/element.types';
import { FunnelSection, SectionColumn, SectionType } from '../types/section.types';

/**
 * Default styles for elements
 */
const defaultElementStyles: ElementStyles = {
  fontSize: '16px',
  fontWeight: '400',
  color: '#000000',
  padding: '10px',
  margin: '0px',
};

/**
 * Create a new element with default properties based on type
 */
export const createDefaultElement = (type: ElementType): FunnelElement => {
  const baseElement = {
    id: uuidv4(),
    type,
    styles: { ...defaultElementStyles },
  };

  switch (type) {
    case 'heading':
      return {
        ...baseElement,
        type: 'heading',
        content: 'New Heading',
        styles: {
          ...defaultElementStyles,
          fontSize: '32px',
          fontWeight: '700',
          textAlign: 'center',
        },
        settings: {
          level: 'h2',
        },
      } as HeadingElement;

    case 'text':
      return {
        ...baseElement,
        type: 'text',
        content: 'Enter your text here...',
        styles: {
          ...defaultElementStyles,
          fontSize: '16px',
          lineHeight: '1.6',
        },
      } as TextElement;

    case 'bulletedList':
      return {
        ...baseElement,
        type: 'bulletedList',
        content: ['List item 1', 'List item 2', 'List item 3'],
        styles: {
          ...defaultElementStyles,
          padding: '10px 10px 10px 30px',
        },
        settings: {
          listStyle: 'disc',
        },
      };

    case 'image':
      return {
        ...baseElement,
        type: 'image',
        content: {
          src: 'https://via.placeholder.com/600x400',
          alt: 'Placeholder image',
        },
        styles: {
          ...defaultElementStyles,
          width: '100%',
          padding: '0px',
        },
        settings: {
          objectFit: 'cover',
        },
      } as ImageElement;

    case 'video':
      return {
        ...baseElement,
        type: 'video',
        content: {
          url: '',
          provider: 'youtube',
        },
        styles: {
          ...defaultElementStyles,
          width: '100%',
          padding: '0px',
        },
        settings: {
          autoplay: false,
          controls: true,
          loop: false,
          muted: false,
        },
      };

    case 'button':
      return {
        ...baseElement,
        type: 'button',
        content: 'Click Me',
        styles: {
          ...defaultElementStyles,
          backgroundColor: '#22c55e',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '6px',
          fontSize: '16px',
          fontWeight: '600',
          textAlign: 'center',
          display: 'inline-block',
        },
        settings: {
          variant: 'primary',
        },
      } as ButtonElement;

    case 'input':
      return {
        ...baseElement,
        type: 'input',
        styles: {
          ...defaultElementStyles,
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          padding: '10px 12px',
        },
        settings: {
          placeholder: 'Enter text...',
          inputType: 'text',
          required: false,
        },
      };

    case 'textarea':
      return {
        ...baseElement,
        type: 'textarea',
        styles: {
          ...defaultElementStyles,
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          padding: '10px 12px',
          minHeight: '100px',
        },
        settings: {
          placeholder: 'Enter your message...',
          rows: 4,
          required: false,
        },
      };

    case 'checkbox':
      return {
        ...baseElement,
        type: 'checkbox',
        styles: {
          ...defaultElementStyles,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        },
        settings: {
          label: 'Checkbox label',
          checked: false,
          required: false,
        },
      };

    case 'select':
      return {
        ...baseElement,
        type: 'select',
        styles: {
          ...defaultElementStyles,
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          padding: '10px 12px',
        },
        settings: {
          label: 'Select an option',
          options: [
            { value: 'option1', label: 'Option 1' },
            { value: 'option2', label: 'Option 2' },
            { value: 'option3', label: 'Option 3' },
          ],
          required: false,
        },
      };

    case 'columnLayout':
      return {
        ...baseElement,
        type: 'columnLayout',
        styles: {
          ...defaultElementStyles,
          display: 'flex',
          gap: '20px',
        },
        settings: {
          columns: 2,
          gap: '20px',
        },
        content: {
          columns: [
            { id: uuidv4(), elements: [] },
            { id: uuidv4(), elements: [] },
          ],
        },
      };

    case 'spacer':
      return {
        ...baseElement,
        type: 'spacer',
        styles: {
          ...defaultElementStyles,
          padding: '0px',
          margin: '0px',
        },
        settings: {
          height: '40px',
        },
      } as SpacerElement;

    case 'divider':
      return {
        ...baseElement,
        type: 'divider',
        styles: {
          ...defaultElementStyles,
          padding: '0px',
          margin: '20px 0px',
        },
        settings: {
          thickness: '1px',
          color: '#e5e7eb',
          style: 'solid',
        },
      } as DividerElement;

    case 'icon':
      return {
        ...baseElement,
        type: 'icon',
        content: {
          iconName: 'Star',
          iconLibrary: 'lucide',
        },
        styles: {
          ...defaultElementStyles,
          fontSize: '24px',
          padding: '0px',
        },
        settings: {
          size: '24px',
        },
      };

    case 'carousel':
      return {
        ...baseElement,
        type: 'carousel',
        content: {
          slides: [
            {
              id: uuidv4(),
              image: 'https://via.placeholder.com/800x400',
              caption: 'Slide 1',
            },
            {
              id: uuidv4(),
              image: 'https://via.placeholder.com/800x400',
              caption: 'Slide 2',
            },
          ],
        },
        styles: {
          ...defaultElementStyles,
          padding: '0px',
        },
        settings: {
          autoplay: false,
          interval: 3000,
          showArrows: true,
          showDots: true,
        },
      };

    case 'accordion':
      return {
        ...baseElement,
        type: 'accordion',
        content: {
          items: [
            {
              id: uuidv4(),
              title: 'Accordion Item 1',
              content: 'Content for item 1',
            },
            {
              id: uuidv4(),
              title: 'Accordion Item 2',
              content: 'Content for item 2',
            },
          ],
        },
        styles: {
          ...defaultElementStyles,
        },
        settings: {
          allowMultiple: false,
        },
      };

    case 'tabs':
      return {
        ...baseElement,
        type: 'tabs',
        content: {
          tabs: [
            {
              id: uuidv4(),
              label: 'Tab 1',
              content: 'Content for tab 1',
            },
            {
              id: uuidv4(),
              label: 'Tab 2',
              content: 'Content for tab 2',
            },
          ],
        },
        styles: {
          ...defaultElementStyles,
        },
      };

    case 'contentBox':
      return {
        ...baseElement,
        type: 'contentBox',
        content: [],
        styles: {
          ...defaultElementStyles,
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
        },
      };

    case 'customHtml':
      return {
        ...baseElement,
        type: 'customHtml',
        content: '<div>Custom HTML content</div>',
        styles: {
          ...defaultElementStyles,
        },
      };

    default:
      return baseElement as FunnelElement;
  }
};

/**
 * Create a new section with default properties and elements
 */
export const createDefaultSection = (type: SectionType = 'content'): FunnelSection => {
  const sectionId = uuidv4();
  
  // Create default elements based on section type
  const getDefaultElements = (): FunnelElement[] => {
    switch (type) {
      case 'header':
        return []; // Header elements will be in separate columns
      
      case 'hero': {
        const heading = createDefaultElement('heading');
        const text = createDefaultElement('text') as any;
        text.content = 'Describe your product or service in a compelling way that captures attention.';
        text.styles = {
          ...text.styles,
          fontSize: '18px',
          lineHeight: '1.6',
          textAlign: 'center',
          marginTop: '20px',
          marginBottom: '30px',
        };
        const button = createDefaultElement('button') as any;
        button.content = 'Get Started';
        button.styles = {
          ...button.styles,
          display: 'inline-block',
          margin: '0 auto',
        };
        return [heading, text, button];
      }
      
      case 'features': {
        const heading = createDefaultElement('heading') as any;
        heading.content = 'Features';
        heading.settings = { level: 'h2' };
        const text = createDefaultElement('text') as any;
        text.content = 'Explore what makes our solution unique.';
        return [heading, text];
      }
      
      case 'testimonials': {
        const heading = createDefaultElement('heading') as any;
        heading.content = 'What Our Customers Say';
        heading.settings = { level: 'h2' };
        return [heading];
      }
      
      case 'pricing': {
        const heading = createDefaultElement('heading') as any;
        heading.content = 'Pricing Plans';
        heading.settings = { level: 'h2' };
        const text = createDefaultElement('text') as any;
        text.content = 'Choose the plan that fits your needs.';
        return [heading, text];
      }
      
      case 'cta': {
        const heading = createDefaultElement('heading') as any;
        heading.content = 'Ready to Get Started?';
        heading.settings = { level: 'h2' };
        const text = createDefaultElement('text') as any;
        text.content = 'Join thousands of satisfied customers today.';
        const button = createDefaultElement('button') as any;
        button.content = 'Sign Up Now';
        return [heading, text, button];
      }
      
      case 'footer': {
        const text = createDefaultElement('text') as any;
        text.content = '© 2025 Your Company. All rights reserved.';
        text.styles = {
          ...text.styles,
          fontSize: '14px',
          textAlign: 'center',
          color: '#6b7280',
        };
        return [text];
      }
      
      default:
        return [];
    }
  };

  // Create columns based on section type
  const createColumns = (): SectionColumn[] => {
    if (type === 'header') {
      // Two columns for header: logo and navigation
      const logoImage = createDefaultElement('image') as any;
      logoImage.content = {
        src: 'https://via.placeholder.com/150x50?text=Logo',
        alt: 'Company Logo',
      };
      logoImage.styles = {
        ...logoImage.styles,
        maxWidth: '150px',
        height: 'auto',
      };

      const homeButton = createDefaultElement('button') as any;
      homeButton.content = 'Home';
      homeButton.styles = {
        ...homeButton.styles,
        marginRight: '10px',
      };

      const featuresButton = createDefaultElement('button') as any;
      featuresButton.content = 'Features';
      featuresButton.styles = {
        ...featuresButton.styles,
        marginRight: '10px',
      };

      const contactButton = createDefaultElement('button') as any;
      contactButton.content = 'Contact';

      return [
        {
          id: uuidv4(),
          span: 4,
          responsive: { tablet: 6, mobile: 12 },
          elements: [logoImage],
        },
        {
          id: uuidv4(),
          span: 8,
          responsive: { tablet: 6, mobile: 12 },
          elements: [homeButton, featuresButton, contactButton],
        },
      ];
    } else if (type === 'hero' || type === 'cta') {
      // Single centered column for hero and CTA
      return [{
        id: uuidv4(),
        span: 12,
        responsive: { tablet: 12, mobile: 12 },
        elements: getDefaultElements(),
      }];
    } else if (type === 'footer') {
      // Three columns for footer
      return [
        {
          id: uuidv4(),
          span: 4,
          responsive: { tablet: 12, mobile: 12 },
          elements: [],
        },
        {
          id: uuidv4(),
          span: 4,
          responsive: { tablet: 12, mobile: 12 },
          elements: getDefaultElements(),
        },
        {
          id: uuidv4(),
          span: 4,
          responsive: { tablet: 12, mobile: 12 },
          elements: [],
        },
      ];
    } else {
      // Single column with default elements
      return [{
        id: uuidv4(),
        span: 12,
        responsive: { tablet: 12, mobile: 12 },
        elements: getDefaultElements(),
      }];
    }
  };

  return {
    id: sectionId,
    type,
    name: `${type.charAt(0).toUpperCase() + type.slice(1)} Section`,
    settings: {
      backgroundColor: type === 'hero' ? '#f8fafc' : type === 'header' ? '#ffffff' : '#ffffff',
      padding: {
        top: type === 'header' ? 20 : type === 'hero' ? 80 : 60,
        right: 20,
        bottom: type === 'header' ? 20 : type === 'hero' ? 80 : 60,
        left: 20,
      },
      margin: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    columns: createColumns(),
  };
};

/**
 * Convert styles object to CSS string
 */
export const stylesToCss = (styles: ElementStyles): string => {
  return Object.entries(styles)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => {
      // Convert camelCase to kebab-case
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      return `${cssKey}: ${value};`;
    })
    .join(' ');
};

/**
 * Get responsive styles based on current breakpoint
 */
export const getResponsiveStyles = (
  baseStyles: ElementStyles,
  responsiveStyles: { tablet?: Partial<ElementStyles>; mobile?: Partial<ElementStyles> } | undefined,
  breakpoint: 'desktop' | 'tablet' | 'mobile'
): ElementStyles => {
  let styles = { ...baseStyles };

  if (breakpoint === 'tablet' && responsiveStyles?.tablet) {
    styles = { ...styles, ...responsiveStyles.tablet };
  }

  if (breakpoint === 'mobile') {
    if (responsiveStyles?.tablet) {
      styles = { ...styles, ...responsiveStyles.tablet };
    }
    if (responsiveStyles?.mobile) {
      styles = { ...styles, ...responsiveStyles.mobile };
    }
  }

  return styles;
};

/**
 * Download a file
 */
export const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

